import React from "react";
import { useCollapse } from "react-collapsed";
import { GoChevronDown } from "react-icons/go";
import { motion } from "framer-motion";

const faqs = [
  {
    q: "How do I find the best airport parking?",
    a: "Compare prices and services from multiple providers instantly.",
  },
  {
    q: "Can I save money?",
    a: "Yes! Save up to 60% versus airport gate prices.",
  },
  {
    q: "Is parking safe?",
    a: "All listed providers are verified with secure facilities.",
  },
  {
    q: "Do you cover my airport?",
    a: "We cover all major UK airports with meet & greet, park & ride, and long-term options.",
  },
];

const FAQItem = ({ question, answer, index }) => {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: true
  });
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className={`mb-4 overflow-hidden transition-all duration-300 ${
        isExpanded 
          ? "bg-gradient-to-br from-primary/5 via-blue-50/50 to-white shadow-lg border border-primary/20" 
          : "bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-white shadow-md hover:shadow-lg border border-gray-200 hover:border-primary/40"
      } rounded-xl transform hover:scale-[1.01]`}
    >
      <button
        {...getToggleProps()}
        className="flex items-center justify-between w-full p-3 text-left transition-all duration-200 focus:outline-none group"
      >
        <div className="flex items-center flex-1 space-x-3">
          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${
            isExpanded 
              ? "bg-gradient-to-r from-primary to-blue-600 text-white shadow-md" 
              : "bg-gray-100 text-gray-500 group-hover:bg-primary/10 group-hover:text-primary"
          }`}>
            Q
          </div>
          <span className={`text-lg font-semibold leading-snug transition-colors duration-200 ${
            isExpanded ? "text-gray-900" : "text-gray-800 group-hover:text-primary"
          }`}>
            {question}
          </span>
        </div>
        <div className={`ml-3 p-2 rounded-full transition-all duration-300 ${
          isExpanded ? "bg-primary/15" : "group-hover:bg-primary/10"
        }`}>
          <GoChevronDown
            className={`transition-all duration-300 ${
              isExpanded ? "rotate-180 text-primary" : "text-gray-400 group-hover:text-primary"
            }`}
            size={20}
          />
        </div>
      </button>
      
      <div {...getCollapseProps()}>
        <div className="px-5 pb-5">
          <div className="flex items-start space-x-3">
            <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-bold text-white rounded-full shadow-sm bg-gradient-to-r from-green-500 to-emerald-600">
              A
            </div>
            <div className="flex-1">
              <div className="px-2 border-green-400 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border-l-3">
                <p className="text-base leading-relaxed text-gray-700">
                  {answer}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FAQSection = () => {
  return (
    <section className="relative py-16 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-64 h-64 rounded-full -top-32 -right-32 bg-gradient-to-r from-primary/10 to-blue-500/10 opacity-60"></div>
        <div className="absolute w-64 h-64 rounded-full -bottom-32 -left-32 bg-gradient-to-r from-purple-500/10 to-primary/10 opacity-60"></div>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6">
        <div className="flex flex-col items-center gap-12 lg:flex-row">
          {/* Left Image */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex justify-center lg:w-1/2"
          >
            <div className="relative group">
              <div className="absolute inset-0 transition-transform duration-500 transform group-hover:scale-110"></div>
              <img
                src={require("../../../common/images/family-travel.webp")}
                alt="Family Vacation FAQ Illustration - Travel Support and Questions"
                className="relative w-full max-w-lg transition-all duration-500 transform group-hover:scale-105"
              />
              <div className="absolute flex items-center justify-center w-16 h-16 rounded-full shadow-xl -bottom-4 -right-4 bg-gradient-to-r from-primary to-blue-500">
                <span className="text-2xl font-bold text-white">?</span>
              </div>
            </div>
          </motion.div>

          {/* Right FAQs */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full lg:w-1/2"
          >
            <div className="mb-8">
             <h2 className="text-3xl font-bold md:text-4xl text-primary ">
          Quick 
          <span className="text-[#00BDBB] "> Faq's</span>
        </h2>
        <div className='flex justify-start py-1 underline'>

<img
                      alt="Decorative underline - FAQ Section Header"
                      src={require("../../../common/images/underline.png")}
                     
                      className="w-[78px] h-[5px]"
                  />

</div>
              <p className="text-lg text-gray-600">
                Find answers to common questions about our services
              </p>
            </div>
            
            <div className="space-y-2">
              {faqs.map((item, index) => (
                <FAQItem 
                  key={index} 
                  question={item.q} 
                  answer={item.a} 
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
